import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { StatsSection } from '../../../statspro/js/components/common/stats-section';

import {
    RoundSelectNavItem,
    CompNavItem,
    ResponsiveSeasonNavItem,
    ResponsiveRoundNavItem,
    LiveToggleNavItem
} from 'common/react/components/competition-nav/nav-items';

import { navSelector } from 'store/modules/competition-nav/selectors/nav-selector';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useNavSync } from 'common/react/components/competition-nav/hooks/use-nav-sync';
import { useWindowSize } from 'common/react/hooks/use-window-resize';
import { useGetCurrentCompFilters } from 'common/react/components/competition-nav/hooks/use-get-current-comp-filters';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { FilterDropdown } from 'widgets/filters/js/components/FilterDropdown';
import { FilterButton } from 'common/react/components/FilterButton';

/**
 * @constant {Array} Competitions we want to appear in the filter for the ladder
 */
export const ALLOWED_COMPS = window.LADDER_DEFAULT_COMP_ORDER
    ? window.LADDER_DEFAULT_COMP_ORDER.split(',')
    : [
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.WOMENS,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.PREMIERSHIP,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.VFL_PREMIERSHIP,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.VFL_WOMENS,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.SANFL_PREMIERSHIP
      ];

export const LadderNav = ({
    competitionId,
    seasonId,
    competitionProviderId,
    seasonPid,
    title
}) => {
    const windowSize = useWindowSize();

    const isResponsive = () =>
        windowSize.width < PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );
    const [areFilterOptionsVisible, setAreFilterOptionsVisible] =
        useState(false);

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
    }, [windowSize.width]);

    const initialValues = {
        competitionId,
        seasonId,
        competitionProviderId,
        seasonPid
    };

    const nav = useSelector(navSelector);

    const options = useCompetitionNavigationOptions(nav);

    useNavSync({ ...options, initialValues, removeFinals: true });

    const competitions = options.competitions;

    const seasons = useSelector(
        (state) =>
            state.seasons[`competition_${nav.competition.id}`]?.list ?? []
    );

    const currentFilters = useGetCurrentCompFilters();
    const competitionNavRef = useRef();

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title,
        filtersState: nav,
        options,
        filtersFields: ['competition', 'season', 'round'],
        filtersRef: competitionNavRef
    });

    const renderDesktop = () => (
        <StatsSection.ScrollableItems>
            <CompNavItem
                classes="fixtures-nav__comp-select"
                competitions={competitions}
                allowListOrder={ALLOWED_COMPS}
                hidePreseason={true}
            />
            <FilterDropdown
                title={title}
                icon="season-trophy"
                navItem="season"
                items={seasons}
            />
            <RoundSelectNavItem
                removeFinals
                className={'u-margin-right-auto'}
            />
            {!showResponsiveItems && currentFilters?.round?.providerId ? (
                <LiveToggleNavItem
                    roundId={currentFilters.round.providerId}
                    className="competition-nav__live-toggle-wrapper competition-nav__live-toggle-wrapper--ladder"
                />
            ) : null}
        </StatsSection.ScrollableItems>
    );

    const renderMobile = () => (
        <>
            <StatsSection.NavItems>
                <CompNavItem
                    classes="fixtures-nav__comp-select"
                    competitions={competitions}
                    allowListOrder={ALLOWED_COMPS}
                    hidePreseason
                    hideLabel
                />
                <FilterButton
                    classes="fixtures-nav__filter-button"
                    onChange={() => setAreFilterOptionsVisible((prev) => !prev)}
                    areFilterOptionsVisible={areFilterOptionsVisible}
                    showFilterLabel={false}
                    showActiveCircle
                />
            </StatsSection.NavItems>
            {areFilterOptionsVisible ? (
                <StatsSection.NavFilterItems>
                    <ResponsiveSeasonNavItem seasons={seasons} />
                    <ResponsiveRoundNavItem removeFinals={true} />
                </StatsSection.NavFilterItems>
            ) : null}
            {showResponsiveItems && currentFilters?.round?.providerId && (
                <StatsSection.ResponsiveRowItems>
                    <LiveToggleNavItem
                        roundId={currentFilters.round.providerId}
                        className="competition-nav__live-toggle-wrapper competition-nav__live-toggle-wrapper--ladder"
                        labelRightPosition
                    />
                </StatsSection.ResponsiveRowItems>
            )}
        </>
    );

    return (
        <div
            className="competition-nav filter-dropdown"
            ref={competitionNavRef}
        >
            <StatsSection.Nav
                withScrollableFilter={!showResponsiveItems}
                useRefinedFilter
            >
                {showResponsiveItems ? renderMobile() : renderDesktop()}
            </StatsSection.Nav>
        </div>
    );
};

LadderNav.propTypes = {
    competitionId: PropTypes.string,
    competitionProviderId: PropTypes.string,
    seasonPid: PropTypes.string,
    seasonId: PropTypes.string,
    title: PropTypes.string
};
